import { ccGetAccountsConfig, ccGetSnapshots, ccPostSnapshot } from '../api-client';
import type { CcAccountConfigRow, CcSnapshot } from '../api-client';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function snapshotAge(asOf: string): string {
  const diffMs = Date.now() - new Date(asOf).getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

let lastSource = 'manual';

export function openBalanceSheet(onDone: () => void, initialAccountId: string | null = null): void {
  const overlay = document.createElement('div');
  document.body.appendChild(overlay);

  overlay.innerHTML = `
    <div class="sheet-backdrop" id="bs-backdrop">
      <div class="sheet balance-sheet" role="dialog" aria-modal="true">
        <header class="sheet-header">
          <h3>Update balances</h3>
          <button class="ghost sheet-close" id="bs-close" type="button" aria-label="Close">✕</button>
        </header>
        <div class="sheet-body" id="bs-body">
          <div class="sk sk-block">Loading…</div>
        </div>
      </div>
    </div>
  `;

  const close = () => { overlay.remove(); };

  overlay.querySelector<HTMLElement>('#bs-backdrop')!.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) close();
  });
  overlay.querySelector<HTMLButtonElement>('#bs-close')!.addEventListener('click', close);

  void loadAndRender(overlay, close, onDone, initialAccountId);
}

async function loadAndRender(
  overlay: HTMLElement,
  close: () => void,
  onDone: () => void,
  initialAccountId: string | null,
): Promise<void> {
  const body = overlay.querySelector<HTMLElement>('#bs-body')!;

  try {
    const [{ accounts }, { snapshots }] = await Promise.all([
      ccGetAccountsConfig(),
      ccGetSnapshots(),
    ]);

    // Latest snapshot per account
    const latestSnapshot = new Map<string, CcSnapshot>();
    for (const s of snapshots) {
      const existing = latestSnapshot.get(s.account_id);
      if (!existing || s.as_of > existing.as_of) {
        latestSnapshot.set(s.account_id, s);
      }
    }

    // Sort: cash-relevant by display_order first, then others alphabetically
    const cashRelevant = accounts
      .filter((a) => a.cash_relevant)
      .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    const others = accounts
      .filter((a) => !a.cash_relevant)
      .sort((a, b) => a.name.localeCompare(b.name));

    const sorted = [...cashRelevant, ...others];

    renderList(body, sorted, latestSnapshot, close, onDone, initialAccountId);
  } catch (e) {
    body.innerHTML = `<div class="cc-error">Couldn't load accounts. <button class="ghost" id="bs-retry">Retry</button></div>`;
    body.querySelector<HTMLButtonElement>('#bs-retry')?.addEventListener('click', () => {
      body.innerHTML = '<div class="sk sk-block">Loading…</div>';
      void loadAndRender(overlay, close, onDone, initialAccountId);
    });
  }
}

function renderList(
  body: HTMLElement,
  accounts: CcAccountConfigRow[],
  latestSnapshot: Map<string, CcSnapshot>,
  close: () => void,
  onDone: () => void,
  initialAccountId: string | null,
): void {
  let selectedId: string | null = accounts.some((a) => a.account_id === initialAccountId) ? initialAccountId : null;

  const snapshotLabel = (accountId: string): string => {
    const snap = latestSnapshot.get(accountId);
    return snap
      ? `${snap.currency} ${snap.balance_native.toLocaleString('en-US')} · ${snapshotAge(snap.as_of)}`
      : 'No snapshot';
  };

  const formHtml = (a: CcAccountConfigRow): string => {
    const snap = latestSnapshot.get(a.account_id);
    const defaultCurrency = snap?.currency ?? 'AED';
    const defaultAsOf = todayIso();
    const defaultSource = lastSource;

    return `
      <div class="bs-form" data-selected-account-id="${escapeHtml(a.account_id)}">
        <button class="ghost bs-back" data-action="back-to-accounts" type="button">Accounts</button>
        <div class="bs-form-head">
          <div class="bs-form-name">${escapeHtml(a.name)}</div>
          <div class="bs-row-snap">${escapeHtml(snapshotLabel(a.account_id))}</div>
        </div>
        <label class="sheet-field">
          <span class="sheet-label">Balance</span>
          <input data-field="balance" type="text" inputmode="decimal" placeholder="0.00" autocomplete="off" />
        </label>
        <label class="sheet-field">
          <span class="sheet-label">Currency</span>
          <input data-field="currency" type="text" value="${escapeHtml(defaultCurrency)}" maxlength="3" />
        </label>
        <label class="sheet-field">
          <span class="sheet-label">As of</span>
          <input data-field="as-of" type="date" value="${escapeHtml(defaultAsOf)}" max="${escapeHtml(todayIso())}" />
        </label>
        <label class="sheet-field">
          <span class="sheet-label">Source</span>
          <input data-field="source" type="text" value="${escapeHtml(defaultSource)}" placeholder="manual" />
        </label>
        <div class="bs-inline-error" data-role="snapshot-error" hidden></div>
        <div class="bs-inline-actions">
          <button class="ghost" data-action="back-to-accounts" type="button">Back</button>
          <button class="primary" data-action="save-snapshot" data-account-id="${escapeHtml(a.account_id)}" type="button">Save</button>
        </div>
      </div>
    `;
  };

  const render = () => {
    const selected = selectedId ? accounts.find((a) => a.account_id === selectedId) ?? null : null;

    if (selected) {
      body.innerHTML = formHtml(selected);
    } else {
      body.innerHTML = `
        <div class="bs-list">
          ${accounts.map((a) => `
            <button class="bs-row" data-account-id="${escapeHtml(a.account_id)}" type="button">
              <span class="bs-row-main">
                <span class="bs-row-name">${escapeHtml(a.name)}</span>
                <span class="bs-row-snap">${escapeHtml(snapshotLabel(a.account_id))}</span>
              </span>
              <span class="sheet-chevron">›</span>
            </button>
          `).join('')}
        </div>
      `;
    }

    body.querySelectorAll<HTMLButtonElement>('.bs-row[data-account-id]').forEach((row) => {
      row.addEventListener('click', () => {
        selectedId = row.getAttribute('data-account-id');
        render();
      });
    });

    body.querySelectorAll<HTMLButtonElement>('[data-action="back-to-accounts"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedId = null;
        render();
      });
    });

    // Save snapshot
    const saveBtn = body.querySelector<HTMLButtonElement>('[data-action="save-snapshot"]');
    saveBtn?.addEventListener('click', async () => {
      const accountId = saveBtn.getAttribute('data-account-id')!;
      const errEl = body.querySelector<HTMLElement>('[data-role="snapshot-error"]')!;
      errEl.hidden = true;

      const balanceRaw = body.querySelector<HTMLInputElement>('[data-field="balance"]')!.value.trim();
      const currency = body.querySelector<HTMLInputElement>('[data-field="currency"]')!.value.trim().toUpperCase() || 'AED';
      const asOf = body.querySelector<HTMLInputElement>('[data-field="as-of"]')!.value.trim() || todayIso();
      const source = body.querySelector<HTMLInputElement>('[data-field="source"]')!.value.trim() || 'manual';

      const balanceNative = parseFloat(balanceRaw);
      if (isNaN(balanceNative)) {
        errEl.textContent = 'Enter a valid balance.';
        errEl.hidden = false;
        return;
      }

      saveBtn.disabled = true;
      try {
        const result = await ccPostSnapshot({ accountId, balanceNative, currency, asOf, source });
        lastSource = source;
        latestSnapshot.set(accountId, result.snapshot);
        selectedId = null;
        render();
        close();
        onDone();
      } catch (e) {
        errEl.textContent = e instanceof Error ? e.message : String(e);
        errEl.hidden = false;
        saveBtn.disabled = false;
      }
    });
  };

  render();
}
